import {
	faFileImage,
	faFilePdf,
	faSpinner,
	faTriangleExclamation,
	faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { formatFileSize } from '@supply-mover-2-0/core';
import {
	formFileAccept,
	formFileLimits,
	formFileTypeWords,
	type FormFieldDefinitionModel,
} from '@supply-mover-2-0/shared';
import type React from 'react';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import { Icon } from '../../base/Icon';
import { FileDropZone } from '../FileDropZone';
import type { FormFileEntryModel } from './formRendererState';

interface FormFileQuestionProps {
	field: FormFieldDefinitionModel;
	entries: ReadonlyArray<FormFileEntryModel>;
	onAdd: (files: Array<File>) => void;
	onRemove: (uploadId: string) => void;
	disabled: boolean;
	/** A single-row drop zone, for a question sharing its row with others. */
	compact: boolean;
	// Injected by FormField: the id the error summary focuses, and the error wiring.
	id?: string;
	'aria-invalid'?: boolean;
	'aria-describedby'?: string;
}

/**
 * PD-16713 — a file upload question: a place to drop files that also opens the file
 * chooser ("Choose files" at phone width, where there is nothing to drag from), and the
 * files picked so far, each uploading, attached, or refused with the reason, and each with
 * a Remove button. The question's kinds, size limit and count are named under the drop
 * zone; a file that breaks one is refused on the page before anything is sent (the server
 * checks again, the kind from the content). Once the question holds its most files the
 * drop zone gives way to a note.
 *
 * Literal token classes only (text-default, border-default, …), as everywhere in the form
 * renderer: the storefront builds its styles by scanning this library's source, and the
 * tokens follow the CRM theme, the storefront brand and the dark registration panel alike.
 */
export function FormFileQuestion({
	field,
	entries,
	onAdd,
	onRemove,
	disabled,
	compact,
	id,
	'aria-invalid': ariaInvalid,
	'aria-describedby': ariaDescribedBy,
}: FormFileQuestionProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const limits = formFileLimits(field);
	const attached = entries.filter((entry) => entry.status !== 'refused').length;
	const isFull = attached >= limits.maxFiles;
	const types = formFileTypeWords(limits.types);
	const hint =
		limits.maxFiles === 1
			? t('{{types}}, up to {{size}} MB.', { types, size: limits.maxMb })
			: t('{{types}}, up to {{size}} MB each. At most {{count}} files.', {
					types,
					size: limits.maxMb,
					count: limits.maxFiles,
				});

	return (
		<div
			id={id}
			role="group"
			aria-label={t(field.label)}
			aria-invalid={ariaInvalid}
			aria-describedby={ariaDescribedBy}
			// Focusable so the error summary's link can move to the question.
			tabIndex={-1}
			className="space-y-2 outline-none"
		>
			{isFull ? (
				<p className={cn('text-xs', colors.classes.textSecondary)}>
					{limits.maxFiles === 1
						? t('Remove the file to attach a different one.')
						: t('Remove a file to attach another.')}
				</p>
			) : (
				<FileDropZone
					compact={compact}
					accept={formFileAccept(limits.types)}
					multiple={limits.maxFiles > 1}
					disabled={disabled}
					label={
						limits.maxFiles > 1
							? t('Drop files here, or choose files')
							: t('Drop a file here, or choose a file')
					}
					phoneLabel={limits.maxFiles > 1 ? t('Choose files') : t('Choose a file')}
					hint={hint}
					onFiles={onAdd}
					// Files of a kind the question does not take are listed as refused, with
					// the reason, rather than disappearing.
					onRejected={onAdd}
				/>
			)}

			{entries.length > 0 && (
				<ul className="space-y-2" aria-live="polite">
					{entries.map((entry) => {
						const isPending =
							entry.status === 'queued' || entry.status === 'uploading';
						const isRefused = entry.status === 'refused';
						return (
							<li
								key={entry.uploadId}
								className={cn(
									'flex items-start gap-3 rounded-md border px-3 py-2',
									isRefused ? 'border-danger' : 'border-default',
									'bg-surface',
								)}
							>
								<Icon
									icon={
										isPending
											? faSpinner
											: isRefused
												? faTriangleExclamation
												: /\.pdf$/i.test(entry.name)
													? faFilePdf
													: faFileImage
									}
									className={cn(
										'mt-0.5 shrink-0',
										isPending && 'fa-spin',
										isRefused
											? colors.classes.textDanger
											: colors.classes.textSecondary,
									)}
								/>
								<div className="min-w-0 flex-1">
									<p className="truncate text-sm text-default">
										{t(entry.name)}
									</p>
									{isRefused ? (
										<p className={cn('text-xs', colors.classes.textDanger)}>
											{t(entry.error ?? '')}
										</p>
									) : (
										<p className={cn('text-xs', colors.classes.textSecondary)}>
											{isPending
												? t('Uploading…')
												: t(formatFileSize(entry.sizeBytes, 1))}
										</p>
									)}
								</div>
								<button
									type="button"
									onClick={() => {
										onRemove(entry.uploadId);
									}}
									disabled={disabled}
									aria-label={t('Remove {{name}}', { name: entry.name })}
									className={cn(
										'-m-1 shrink-0 rounded p-1 focus:outline-none focus:ring-2 focus:ring-accent',
										colors.classes.textSecondary,
										disabled ? 'cursor-not-allowed opacity-50' : 'hover:text-default',
									)}
								>
									<Icon icon={faXmark} />
								</button>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
}
